import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import EncounterGate from "@/components/chart/EncounterGate";
import StickyNotesPanel from "@/components/chart/StickyNotesPanel";
import TelehealthVisits from "@/components/chart/TelehealthVisits";
import { listStickyNotes, isClosed as stickyClosed } from "@/lib/stickyNotes";
import { makeLuminaHost } from "@/lib/luminaHost";
import { useChartTabs } from "@/lib/chartTabs";
import { useAuth } from "@/lib/auth";
import { logChartLoad } from "@/lib/traffic";
import { fetchChartById } from "@/lib/chartCache";
import { loadTovContext } from "@/lib/dictation";
import { chartIngest, getFileContent, listAnalyses, getAnalysis } from "@/lib/patientDocs";
import { getNote, signNote, unsignNote, generateClaim, recodeFromNote } from "@/lib/noteClaim";
import { patchNoteSoap } from "@/lib/dictation";
import { reformatNote } from "@/lib/chartInsert";
import { claimView, bandOf, blockersSet, stageOf2, isGenerated } from "@/lib/createClaimsModel";
import LensCard from "@/components/patientdocs/pdLens";
import { AiMarkdown, EchoMessages, AiSteps, AI_PANEL_CSS } from "@/components/chart/AiPanelBody";
import { AiProgress, chartReadTerms, AI_PROGRESS_CSS } from "@/components/chart/AiProgress";
import { VisitScreen } from "@/components/telehealthv2/THVisit";
import { THDock, dockInitial } from "@/components/telehealthv2/THDock";

// Patients V2 chart host. Embeds the API-backed OneChart v2 prototype
// (public/onechart_v2.html) and drives it to the selected patient. Unlike Chart.jsx,
// this file does NOT normalise fields, correct coverage, stamp samples, relocate the
// sticky, or mount the booker/encounters iframes — onechart_v2.html does all of that
// natively (see handoff §1.4/§3). We only: point EHI at the proxy, bind the payload,
// honour the tab, and bind the optional side feeds.
const PROXY_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/proxy`;

export default function ChartV2({ uuid: uuidProp, embedded = false, program: programProp, onChartingChange, targetTab, autoEncounter = false, errand: errandProp = null, active = true, onTeleBack, onTeleNoteReady } = {}) {
  const [params] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();
  const returnTo = params.get("return");
  const uuid = uuidProp || routeParams.chartUuid || params.get("uuid") || params.get("chart_uuid") || "";
  const urlNote = !uuidProp ? params.get("note") : null;
  const errand = errandProp || (urlNote ? { note_id: urlNote, edit: params.get("edit") === "1", n: "url" } : null);
  const program = (programProp || params.get("program") || "APCM").toUpperCase();
  const tab = (targetTab && targetTab.tab) || params.get("tab") || "";
  const tabNonce = targetTab ? targetTab.n : null;

  const iframeRef = useRef(null);
  const { openNote } = useChartTabs();
  const { user } = useAuth();
  // Per-chart load-time stat: start a clock when a chart opens, log once it's ready (app-wide).
  const loadClock = useRef(0);
  const loadLogged = useRef(false);
  const cacheHitRef = useRef(false);
  useEffect(() => { loadClock.current = (typeof performance !== "undefined" ? performance.now() : Date.now()); loadLogged.current = false; }, [uuid]);
  const [teleW, setTeleW] = useState(dockInitial);
  const teleDrag = (v) => { try { iframeRef.current?.contentWindow?.document.body.classList.toggle("ap-drag", v); } catch (e) { /* frame not ready */ } };
  const prefetchRef = useRef(null);
  const lastChartingRef = useRef(false);
  const onChartingChangeRef = useRef(onChartingChange);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [errMsg, setErrMsg] = useState("");
  const [patientName, setPatientName] = useState("");
  const [leaving, setLeaving] = useState(false);
  const [encGate, setEncGate] = useState(null); // {providers, defaultNpi, defaultDos, resolve, reject}
  const [sticky, setSticky] = useState(null); // {clientId, chartId, chartUuid} when the sticky-notes panel is open
  const [tele, setTele] = useState(null); // {clientId, ctx, snapshot} when the telehealth panel is open
  const [docView, setDocView] = useState(null); // {loading, dataUrl, content_type, filename, error}
  const [insight, setInsight] = useState(null); // {tab:'timeline'|'changes', loading, timeline:[], changes:[]}
  const winRef = useRef(null);
  const autoEncRef = useRef(false);
  const docsRef = useRef([]);
  const encClaimsRef = useRef([]);
  const dRef = useRef(null);
  const insightRef = useRef(null);
  const goBack = () => { setLeaving(true); setTimeout(() => navigate(returnTo), 180); };
  // A note saved in another tab (Encounter review editor) → refresh this chart's encounter ↔ claim map.
  const rebindRef = useRef(null);
  useEffect(() => {
    const h = () => rebindRef.current?.();
    window.addEventListener("revq:expand-encounter", h);
    return () => window.removeEventListener("revq:expand-encounter", h);
  }, []);

  // Sticky-notes active-count badge on the chart's "Sticky note" button (lives in the iframe).
  const pushStickyCount = useCallback((n) => {
    try { const w = winRef.current; if (w && typeof w.ehiSetStickyCount === "function") w.ehiSetStickyCount(n); } catch (e) {}
  }, []);
  const refreshStickyCount = useCallback(async () => {
    try { if (!uuid) return; const r = await listStickyNotes(uuid); const act = (r.notes || []).filter((x) => !stickyClosed(x.status)).length; pushStickyCount(act); } catch (e) {}
  }, [uuid, pushStickyCount]);

  // In-chart document viewer: pretty extracted view (Lens) + the original file beneath.
  const viewDoc = async (analysisId, documentId, filename) => {
    if (!analysisId && !documentId) return;
    setDocView({ loading: true, filename: filename || "Document" });
    try {
      let a = null;
      try { a = await getAnalysis(analysisId); } catch (e) {}
      const docId = (a && a.document_id) || documentId;
      let dataUrl = "", ct = "";
      if (docId) {
        try { const f = await getFileContent(docId); const b64 = f.file_content || f.base64 || ""; ct = f.content_type || "application/pdf"; dataUrl = `data:${ct};base64,${b64}`; } catch (e) {}
      }
      setDocView({ loading: false, filename: (a && a.filename) || filename || "Document", analysis: a, dataUrl, content_type: ct });
    } catch (e) {
      setDocView({ loading: false, filename: filename || "Document", error: "Could not load this document." });
    }
  };

  // Re-pull the document analyses and re-bind the chart's Documents tab (keeps ingest status fresh).
  const refreshDocs = async (win, D, uuid) => {
    try {
      const ch = (D && D.chart) || {};
      const cid = ch.client_id || ""; const mrn = ch.mrn || "";
      if (!cid) return;
      const j = await listAnalyses({ client_id: cid, limit: 300, etag: true });
      const all = (j && (j.analyses || [])) || [];
      const pick = (o, keys) => { for (const k of keys) { const v = o && o[k]; if (v !== undefined && v !== null && String(v).trim() !== "") return v; } return ""; };
      const fmt = (s) => { if (!s) return ""; const d = new Date(s); return isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
      const rows = all
        .filter((a) => { const p = ((a.edi_client_match || {}).patient) || {}; return (mrn && p.mrn === mrn) || (uuid && a.linked_chart_uuid === uuid); })
        .map((a) => {
          const ext = a.extraction || {};
          const rawDos = pick(ext, ["encounter_date", "date_of_service", "visit_date", "collection_date", "collected_at", "resulted_at", "result_date", "study_date", "exam_date", "service_date", "document_date", "effective_date"]);
          return { id: a.id, document_id: a.document_id || a.id, name: a.filename || a.file_name || "Document", type: a.document_type || "other", dos: fmt(rawDos), dosRaw: rawDos ? (new Date(rawDos).getTime() || 0) : 0, provider: pick(ext, ["provider_name", "rendering_provider", "ordering_provider", "provider", "physician", "attending"]), link: a.edi_link_status || "", chart_status: a.chart_data_status || a.edi_chart_status || "ready", ingested: (a.chart_data_status === "ingested" || a.edi_chart_status === "ingested"), size: a.file_size || 0, uploaded: a.created_at || "" };
        })
        .sort((x, y) => y.dosRaw - x.dosRaw);
      docsRef.current = rows;
      if (typeof win.ehiBindDocs === "function") win.ehiBindDocs(rows);
    } catch (e) {}
  };

  // Add a document to the chart from the Documents tab (optimistic Adding… then refresh).
  const ingestDoc = async (analysisId) => {
    const win = winRef.current; if (!win || !analysisId) return;
    try {
      const next = docsRef.current.map((d) => (d.id === analysisId ? { ...d, chart_status: "ingesting", ingested: false } : d));
      docsRef.current = next;
      if (typeof win.ehiBindDocs === "function") win.ehiBindDocs(next);
      await chartIngest(analysisId);
      const D = dRef.current;
      setTimeout(() => refreshDocs(win, D, uuid), 2500);
      setTimeout(() => refreshDocs(win, D, uuid), 6500);
    } catch (e) {
      const D = dRef.current; refreshDocs(win, D, uuid);
    }
  };


  // The host asks the UI for the dictating provider + date of service before capture.
  const requestEncounterMeta = ({ providers, defaultNpi, defaultDos }) =>
    new Promise((resolve, reject) => setEncGate({ providers, defaultNpi, defaultDos, resolve, reject }));

  // Timeline + Chart change-history (DEPLOYED_CHANGES §2.4/§2.6). Both read from the
  // new EHI-bridge chart endpoints by client_id + chart_id (NOT the uuid).
  const openInsights = async (initialTab) => {
    const D = dRef.current || {}; const ch = D.chart || {};
    const cid = ch.client_id || ""; const chartId = ch.chart_id || "";
    const token = sessionStorage.getItem("revq.token") || "";
    const hdr = { Authorization: `Bearer ${token}` };
    setInsight({ tab: initialTab || "timeline", loading: true, timeline: [], changes: [] });
    let timeline = [], changes = [];
    try { const r = await fetch(`${PROXY_BASE}/api/ehibridge/import/charts/${encodeURIComponent(cid)}/${encodeURIComponent(chartId)}/timeline`, { headers: hdr }); const j = await r.json(); timeline = (j && j.items) || []; } catch (e) {}
    try { const r = await fetch(`${PROXY_BASE}/api/ehibridge/import/change-logs/${encodeURIComponent(cid)}/${encodeURIComponent(chartId)}`, { headers: hdr }); const j = await r.json(); changes = (j && j.change_logs) || []; } catch (e) {}
    setInsight((p) => (p ? { ...p, loading: false, timeline, changes } : p));
  };

  // §2.7 AI panels — three Server-Sent-Event streams over the merged chart. The user
  // picks one; nothing runs automatically. HTTP 402 → required low-credit message.
  const AI_PANELS = [
    { key: "summary", label: "Clinical Summary", desc: "Concise clinical overview across every source", method: "GET", path: (u) => `chart-summary-stream/${encodeURIComponent(u)}` },
    { key: "lumina", label: "Lumina Extreme", desc: "Deep multi-source analysis & risk signals", method: "GET", path: (u) => `lumina-extreme-stream/${encodeURIComponent(u)}` },
    { key: "echo", label: "Patient Echo", desc: "Plain-language recap for the patient", method: "POST", path: (u) => `patient-echo-stream/${encodeURIComponent(u)}` },
  ];
  const openAi = (panelKey) => setInsight({ tab: "ai", loading: false, timeline: [], changes: [], ai: { panel: panelKey, state: "idle", text: "" } });
  insightRef.current = (k) => (k === "timeline" ? openInsights("timeline") : openAi(k));
  const streamAi = async (panelKey) => {
    const def = AI_PANELS.find((x) => x.key === panelKey); if (!def) return;
    const token = sessionStorage.getItem("revq.token") || "";
    setInsight((p) => (p ? { ...p, ai: { panel: panelKey, state: "streaming", text: "" } } : p));
    try {
      const resp = await fetch(`${PROXY_BASE}/api/ehibridge/import/${def.path(uuid)}`, {
        method: def.method,
        headers: { Authorization: `Bearer ${token}`, ...(def.method === "POST" ? { "Content-Type": "application/json" } : {}) },
        ...(def.method === "POST" ? { body: "{}" } : {}),
      });
      if (resp.status === 402) { setInsight((p) => (p ? { ...p, ai: { panel: panelKey, state: "nocredit", text: "" } } : p)); return; }
      if (!resp.ok || !resp.body) { setInsight((p) => (p ? { ...p, ai: { panel: panelKey, state: "error", text: "" } } : p)); return; }
      const reader = resp.body.getReader(); const dec = new TextDecoder(); let buf = "", acc = "", lang = ""; const steps = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop();
        for (const ln of lines) {
          const m = ln.match(/^data:\s*(.*)$/); if (!m) continue;
          try {
            const o = JSON.parse(m[1]);
            if (o.text) acc += o.text;
            if (o.swing && o.label) steps.push({ n: o.swing, label: o.label, done: false });
            if (o.swing_done) steps.forEach((st) => { if (st.n === o.swing_done) st.done = true; });
            if (o.patient_language) lang = o.patient_language;
          } catch (e) {}
        }
        setInsight((p) => (p && p.ai && p.ai.panel === panelKey ? { ...p, ai: { ...p.ai, text: acc, steps: steps.map((x) => ({ ...x })), lang } } : p));
      }
      setInsight((p) => (p && p.ai && p.ai.panel === panelKey ? { ...p, ai: { ...p.ai, state: "done", text: acc } } : p));
    } catch (e) { setInsight((p) => (p ? { ...p, ai: { panel: panelKey, state: "error", text: "" } } : p)); }
  };

  // §2.5 arrival: chart bound → adopt the note (a GET) → guards → note panel → edit state.
  const errandKey = errand ? `${errand.note_id || ""}|${errand.n || ""}` : "";
  const runNoteErrand = useCallback(async (er) => {
    const win = winRef.current; if (!win || !er || !er.note_id) return;
    const bar = (m, retry) => { try { win.ntcBar(m, retry); } catch (e) {} };
    let note = null;
    try { note = await getNote(er.note_id); }
    catch (e) { if (e?.response?.status === 404) bar("That visit no longer exists."); else bar("The visit could not be loaded.", () => runNoteErrand(er)); return; }
    if (!note) { bar("That visit no longer exists."); return; }
    const ch = (dRef.current && dRef.current.chart) || {};
    const norm = (x) => String(x || "").replace(/\s+/g, " ").trim().toLowerCase();
    const chartName = norm([ch.first_name, ch.last_name].filter(Boolean).join(" "));
    const same = note.patient_mrn && ch.mrn ? norm(note.patient_mrn) === norm(ch.mrn) : (!!chartName && norm(note.patient_name) === chartName);
    if (!same) { bar("This visit is recorded against a different patient. Open it from Encounter review to check."); return; }
    const lock = note.signed ? "signed" : !note.encounter ? "nodoc" : null;
    try { win.apAdoptNote(note, { edit: !!er.edit, lock, id: er.note_id }); } catch (e) {}
  }, []);
  // §3.5 the chart body opens on Focus when the call starts — once, on that transition only.
  const teleErrand = errand && errand.telehealth ? errand : null;
  const teleFocused = useRef(null);
  useEffect(() => {
    if (state !== "ready" || !teleErrand || teleFocused.current === teleErrand.n) return;
    teleFocused.current = teleErrand.n;
    try { const w = winRef.current; if (w && typeof w.setTab === "function") w.setTab("focus"); } catch (e) {}
  }, [state, teleErrand]);
  useEffect(() => {
    if (state !== "ready" || !errand || !errand.note_id) return;
    runNoteErrand(errand);
  }, [state, errandKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { onChartingChangeRef.current = onChartingChange; });

  // Lift the file's `charting` body class up so the revQ sidebar collapses to icons.
  useEffect(() => {
    if (state !== "ready") return undefined;
    const read = () => {
      try {
        const b = iframeRef.current && iframeRef.current.contentWindow && iframeRef.current.contentWindow.document.body;
        const on = !!(b && b.classList.contains("charting"));
        if (on !== lastChartingRef.current) { lastChartingRef.current = on; onChartingChangeRef.current && onChartingChangeRef.current(on); }
      } catch (e) {}
    };
    read();
    const id = setInterval(read, 400);
    return () => clearInterval(id);
  }, [state]);

  useEffect(() => () => { try { onChartingChangeRef.current && onChartingChangeRef.current(false); } catch (e) {} }, []);

  // Honour the requested tab after bind (native setTab), retrying on re-activation.
  useEffect(() => {
    if (state !== "ready" || !tab || tab === "focus") return undefined;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      try {
        const win = iframeRef.current && iframeRef.current.contentWindow;
        // "engagement-docs" = Engagement tab, Documents list (Encounter review → chart document).
        if (win && typeof win.setTab === "function") win.setTab(tab === "engagement-docs" ? "engagement" : tab);
        if (tab === "engagement-docs" && win && typeof win.apEncListTab === "function") win.apEncListTab("docs");
      } catch (e) {}
      if (tries > 20) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [state, tab, tabNonce]);

  // Log chart load time (login + chartUUID + client) the first time this chart reaches "ready".
  useEffect(() => {
    if (state !== "ready" || loadLogged.current || !uuid) return;
    loadLogged.current = true;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const ms = loadClock.current ? Math.max(0, Math.round(now - loadClock.current)) : 0;
    const ch = (dRef.current && dRef.current.chart) || {};
    logChartLoad({ email: user?.email || "", chart_uuid: uuid, client_id: ch.client_id || "", duration_ms: ms, cached: cacheHitRef.current });
  }, [state, uuid, user]);


  useEffect(() => {
    if (state !== "ready" || !autoEncounter || autoEncRef.current) return undefined;
    autoEncRef.current = true;
    const t = setTimeout(() => {
      try { const win = iframeRef.current && iframeRef.current.contentWindow; if (win && typeof win.chartToggle === "function" && !win.CHARTING) win.chartToggle(); } catch (e) {}
    }, 450);
    return () => clearTimeout(t);
  }, [state, autoEncounter]);

  // Kick the chart fetch off on mount so it runs concurrently with the iframe load.
  useEffect(() => {
    document.title = "Patient Chart · revQ";
    const token = sessionStorage.getItem("revq.token") || "";
    if (!uuid || !token) return;
    prefetchRef.current = fetchChartById(PROXY_BASE, uuid, token).catch(() => null);
  }, [uuid]);

  const bindSideFeed = async (win, path, method) => {
    try {
      const token = sessionStorage.getItem("revq.token") || "";
      const r = await fetch(`${PROXY_BASE}${path}`, { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const j = await r.json();
      if (typeof win[method] === "function") win[method](j);
    } catch (e) {}
  };

  // Fetch the patient's note-to-claim notes and hand the chart a DOS-keyed claim map
  // so an encounter can link to its revQ claim (encounter → claim cross-link).
  const bindEncClaims = async (win, D, token, uuid) => {
    try {
      const ch = (D && D.chart) || {};
      const cid = ch.client_id || "";
      const want = [ch.first_name, ch.last_name].filter(Boolean).join(" ").trim().toLowerCase();
      const qs = new URLSearchParams({ limit: "300" });
      if (cid) qs.set("client_id", cid);
      const r = await fetch(`${PROXY_BASE}/api/edi/note-to-claim/notes?${qs.toString()}`,
        { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const j = await r.json();
      const notes = (j && (j.notes || j.results || j.items)) || [];
      const norm = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase();
      const rows = [];
      for (const n of notes) {
        if (!((uuid && n.chart_uuid === uuid) || (want && norm(n.patient_name) === want))) continue;
        const id = n.id || n.note_id; const dos = n.date_of_service || n.dos || "";
        if (!id || !dos) continue;
        let detail = null;
        try { const dd = await getNote(id); detail = (dd && (dd.note || dd)) || null; } catch (e) {}
        const enc = (detail && detail.encounter) || {};
        const soap = enc.soap_note || {};
        // Mirror /create-claims status exactly: Claim generated → Ready to submit → Needs review → Blocked.
        let generated = false, claimStage = "Blocked";
        try {
          if (detail) {
            const v = claimView(detail);
            const e2 = { n: detail, v, band: bandOf(detail, v), blk: blockersSet(detail, v) };
            generated = isGenerated(detail);
            const st = stageOf2(e2);
            claimStage = st === "ready" ? "Ready to submit" : st === "review" ? "Needs review" : "Blocked";
          }
        } catch (e) {}
        rows.push({
          id, client_id: n.client_id || cid, dos,
          visit_id: (detail && (detail.visit_id || detail.claim_number)) || n.visit_id || n.claim_number || "",
          generated, claimStage,
          signed: (detail && detail.signed) || n.signed || false,
          signed_at: (detail && detail.signed_at) || n.signed_at || null,
          signed_by: (detail && detail.signed_by) || n.signed_by || null,
          status: (detail && detail.status) || (n.claim_readiness && n.claim_readiness.status) || n.claim_status || "",
          source: (detail && detail.visit_source) || n.visit_source || "",
          provider: (detail && detail.provider_name) || n.provider_name || "",
          npi: (detail && (detail.provider_npi || detail.npi_number)) || n.npi_number || "",
          pos: (detail && detail.place_of_service) || null,
          icd: (((detail && detail.icd_codes) || [])).map((c) => ({ code: c.icd_dotted || c.code, desc: c.description, valid: c.valid, billable: c.billable, provenance: c.provenance })),
          cpt: (((detail && detail.cpt_codes) || [])).map((c) => ({ code: c.code, desc: c.description, dx: (c.dx || []).map((d) => d.code), paid: (c.avg_paid && c.avg_paid.amt) || null })),
          note: {
            body: enc.formatted_note || enc.soap_text || "",
            rx: ((enc.prescriptions || [])).map((m) => ({ name: m.drug_name || m.name || "", strength: m.strength || "", sig: m.sig || "", days: m.days_supply, refills: m.refills, qty: m.total_qty })),
            pi: (((enc.patient_instructions || {}).sections) || []).map((s) => ({ title: s.title_en || s.title || "", body: s.body_en || s.body || "" })),
            msgs: (((enc.patient_comms || {}).messages) || []).map((m) => ({ when: m.send_at || "", label: m.label || "", text: m.text_en || m.text || "" })),
            transcript: enc.transcript || "",
            generated_at: enc.generated_at || "",
          },
        });
      }
      if (typeof win.ehiBindEncClaims === "function") win.ehiBindEncClaims(rows);
      encClaimsRef.current = rows;
    } catch (e) {}
  };

  // Fetch the document analyses tied to this patient (by MRN / chart uuid) and hand the
  // chart its Documents tab — the chart-by-id payload carries only ingestion refs, not the
  // full document list, so we read the same analyses feed the Chart Documents page uses.
  const bindDocs = async (win, D, token, uuid) => {
    try {
      const ch = (D && D.chart) || {};
      const cid = ch.client_id || "";
      const mrn = ch.mrn || "";
      if (!cid) return;
      const j = await listAnalyses({ client_id: cid, skip: 0, limit: 300, etag: true });
      const all = (j && (j.analyses || j.results || j.items)) || [];
      const pick = (o, keys) => { for (const k of keys) { const v = o && o[k]; if (v !== undefined && v !== null && String(v).trim() !== "") return v; } return ""; };
      const fmt = (s) => { if (!s) return ""; const d = new Date(s); return isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
      const rows = all
        .filter((a) => {
          const p = ((a.edi_client_match || {}).patient) || {};
          return (mrn && p.mrn === mrn) || (uuid && a.linked_chart_uuid === uuid);
        })
        .map((a) => {
          const ext = a.extraction || {};
          const rawDos = pick(ext, ["encounter_date", "date_of_service", "visit_date", "collection_date", "collected_at", "resulted_at", "result_date", "study_date", "exam_date", "service_date", "document_date", "effective_date"]);
          return {
            id: a.id,
            document_id: a.document_id || a.id,
            name: a.filename || a.file_name || "Document",
            type: a.document_type || "other",
            dos: fmt(rawDos),
            dosRaw: rawDos ? (new Date(rawDos).getTime() || 0) : 0,
            provider: pick(ext, ["provider_name", "rendering_provider", "ordering_provider", "provider", "physician", "attending"]),
            link: a.edi_link_status || "",
            chart_status: a.chart_data_status || a.edi_chart_status || "ready",
            ingested: (a.chart_data_status === "ingested" || a.edi_chart_status === "ingested"),
            size: a.file_size || 0,
            uploaded: a.created_at || "",
          };
        })
        .sort((x, y) => y.dosRaw - x.dosRaw);
      docsRef.current = rows;
      if (typeof win.ehiBindDocs === "function") win.ehiBindDocs(rows);
    } catch (e) {}
  };

  // §2.3 — per-encounter data indicator dots. POST the encounter ids, bind {id:[labels]}.
  const bindIndicators = async (win, D, token) => {
    try {
      const encs = (D && (D.encounters || D.encs)) || [];
      const ids = encs.map((e) => (e && (e.encounter_id || e.id))).filter(Boolean).slice(0, 200);
      if (!ids.length || typeof win.ehiBindIndicators !== "function") return;
      const r = await fetch(`${PROXY_BASE}/api/ehibridge/import/encounters/indicators`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ encounter_ids: ids }),
      });
      if (!r.ok) return;
      const map = await r.json();
      win.ehiBindIndicators(map && typeof map === "object" ? map : {});
    } catch (e) {}
  };

  const drive = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const token = sessionStorage.getItem("revq.token") || "";
    let tries = 0;

    const tick = async () => {
      tries += 1;
      let win;
      try { win = iframe.contentWindow; } catch (e) { return; }
      const ready = win && typeof win.ehiChart === "function" && typeof win.ehiBind === "function" && win.EHI;
      if (!ready) {
        if (tries < 150) setTimeout(tick, 100);
        else { setErrMsg("The chart failed to initialize."); setState("error"); }
        return;
      }
      if (!uuid) { setErrMsg(urlNote ? "That link is missing the chart or the visit." : "No patient chart was specified."); setState("error"); return; }
      if (!token) { setErrMsg("Your session has expired. Please sign in again."); setState("error"); return; }

      win.EHI.base = PROXY_BASE;
      win.EHI.token = token;
      win.EHI.program = program;
      win.EHI.uuid = uuid;
      // NOTE: AP_HIDE_STATES intentionally left unset (keep the dev state switcher).
      // LUMINA_HOST (encounter panel) is wired in a later pass.

      try {
        let pre = prefetchRef.current ? await prefetchRef.current : null;
        let D = pre ? pre.data : null;
        cacheHitRef.current = !!(pre && pre.cached);
        if (!D) { D = await win.ehiChart(uuid); cacheHitRef.current = false; }
        // TOV: the client's list + provider templates (never invented; empty → empty state)
        try { Object.assign(D, await loadTovContext((D && D.chart && D.chart.client_id) || "")); } catch (e) {}
        // Wire the encounter panel host BEFORE bind (dictation / voice / SOAP / send-to-claim).
        try { win.LUMINA_HOST = makeLuminaHost({ D, uuid, token, requestEncounterMeta, onOpenClaim: (claimId, cid) => { setLeaving(true); setTimeout(() => navigate(`/create-claims?claim=${encodeURIComponent(claimId)}&client=${encodeURIComponent(cid || "")}`), 180); } }); } catch (e) {}
        win.ehiBind(D);
        try { if (tab && typeof win.setTab === "function") win.setTab(tab); } catch (e) {}
        // Optional side feeds (handoff §4) — each in its own try/catch, cleared on bind.
        await bindSideFeed(win, `/api/dictation/represcribe-alerts?patient_key=${encodeURIComponent(uuid)}`, "ehiBindRepresc");
        // DQ-11: engagement "why" is bound to the CONFIRMED patient — send client_id + chart ids + dob/gender.
        // Without them upstream treats the patient as unconfirmed and strips age/gender from every reason.
        {
          const ch = (D && D.chart) || {};
          const eng = new URLSearchParams({ patient_key: uuid });
          if (uuid) eng.set("chart_uuid", uuid);
          if (ch.client_id) eng.set("client_id", ch.client_id);
          if (ch.chart_id) eng.set("chart_id", ch.chart_id);
          if (ch.dob) eng.set("dob", ch.dob);
          const g = ch.sex || ch.gender; if (g) eng.set("gender", g);
          await bindSideFeed(win, `/api/dictation/engagement-events?${eng.toString()}`, "ehiBindEngagement");
        }
        try {
          const c = (D && D.chart) || {};
          const nm = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
          if (nm) setPatientName(nm.replace(/\b\w/g, (m) => m.toUpperCase()).replace(/\B\w/g, (m) => m.toLowerCase()));
        } catch (e) {}
        // Encounter → claim cross-link: navigation callbacks + the patient's note-to-claim notes.
        try {
          win.ENC_OPEN_CLAIM = (claimId, cid) => { setLeaving(true); setTimeout(() => navigate(`/create-claims?claim=${encodeURIComponent(claimId || "")}&client=${encodeURIComponent(cid || "")}`), 180); };
          win.ENC_OPEN_CLAIMS = (cid) => { setLeaving(true); setTimeout(() => navigate(`/create-claims${cid ? `?client=${encodeURIComponent(cid)}` : ""}`), 180); };
          win.ENC_VIEW_DOC = (analysisId, docId, filename) => viewDoc(analysisId, docId, filename);
          win.ENC_INGEST_DOC = (analysisId) => ingestDoc(analysisId);
          win.ENC_FETCH_DETAIL = async (eid) => {
            const r = await fetch(`${PROXY_BASE}/api/ehibridge/import/encounters/${encodeURIComponent(eid)}`, { headers: { Authorization: `Bearer ${sessionStorage.getItem("revq.token") || ""}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json();
          };
          win.CV2_INSIGHT = (k) => insightRef.current && insightRef.current(k);
          // NTC — the visit's note (note-to-claim). DX_REBUILD is the single crossing into dictation's engine.
          win.NTC_GET_NOTE = (id) => getNote(id);
          win.NTC_SAVE_NOTE = (id, fields) => patchNoteSoap(id, fields);
          win.NTC_RECODE = (id) => recodeFromNote(id);
          win.NTC_NOTE_SAVED = (id) => window.dispatchEvent(new CustomEvent("revq:note-saved", { detail: { noteId: id } }));
          win.NTC_OPEN_EDITOR = (id, u, name) => { if (openNote) openNote({ noteId: id, chartUuid: u || uuid, name: name || "Patient", fromLabel: "Chart" }); };
          win.DX_REBUILD = (text, picks, note) => {
            const enc = (note && note.encounter) || {};
            return reformatNote(text, picks, { labeled: true, sessionId: enc.dictation_session_id, clientId: (note && note.client_id) || ((D && D.chart) || {}).client_id, npi: (note && (note.npi_number || note.provider_npi)) || undefined, specialty: (note && note.taxonomy_desc) || undefined, patientId: uuid });
          };
          // Sticky Notes: the chart's "Sticky note" button opens the React panel (API-backed, per-patient).
          win.STICKY_OPEN = () => { const ch = (dRef.current && dRef.current.chart) || {}; setSticky({ clientId: ch.client_id || "", chartId: ch.chart_id || ch.mrn || "", chartUuid: uuid }); };
          // Telehealth visits panel (provider side) — launched from the Engagement booking area.
          win.TELE_OPEN = () => {
            const D = dRef.current || {}; const ch = D.chart || {};
            const arr = (x) => Array.isArray(x) ? x : [];
            const pick = (o, ks) => { for (const k of ks) { const v = o && o[k]; if (v) return v; } return ""; };
            const snapshot = {
              conditions: arr(D.problems || ch.problems).map((p) => pick(p, ["description", "name", "problem", "title", "code"])).filter(Boolean).slice(0, 10),
              meds: arr(D.medications || ch.medications).map((m) => pick(m, ["name", "drug", "medication", "description", "title"])).filter(Boolean).slice(0, 10),
              allergies: arr(D.allergies || ch.allergies).map((a) => pick(a, ["name", "allergen", "substance", "description"])).filter(Boolean).slice(0, 10),
              vitals: arr(D.vitals || ch.vitals).map((v) => pick(v, ["label", "name", "value", "display"])).filter(Boolean).slice(0, 8),
            };
            const nm = [ch.first_name, ch.last_name].filter(Boolean).join(" ").trim() || ch.patient_name || ch.name || patientName || "";
            setTele({ clientId: ch.client_id || "", ctx: { uuid, mrn: ch.mrn || ch.chart_id || "", chartId: ch.chart_id || "", name: nm, dob: ch.dob || ch.date_of_birth || "" }, snapshot });
          };
          // Signed visits: sign / unsign / generate-claim from the encounter drawer. Each
          // patches the bound claim row in place and re-binds so the drawer updates live.
          const patchEncClaim = (id, patch) => {
            const rows = (encClaimsRef.current || []).map((r) => (String(r.id) === String(id) ? { ...r, ...patch } : r));
            encClaimsRef.current = rows;
            try { if (typeof win.ehiBindEncClaims === "function") win.ehiBindEncClaims(rows); } catch (e) {}
          };
          win.ENC_SIGN_VISIT = async (id) => { try { const r = await signNote(id); patchEncClaim(id, { signed: true, signed_at: r?.signed_at || new Date().toISOString(), signed_by: r?.signed_by || null }); return { ok: true }; } catch (e) { const d = e?.response?.data?.detail; throw new Error(typeof d === "string" ? d : "The visit could not be signed."); } };
          win.ENC_UNSIGN_VISIT = async (id) => { try { await unsignNote(id); patchEncClaim(id, { signed: false, signed_at: null, signed_by: null }); return { ok: true }; } catch (e) { const d = e?.response?.data?.detail; throw new Error(typeof d === "string" ? d : "The visit could not be unsigned."); } };
          win.ENC_GENERATE_CLAIM = async (id) => {
            try {
              const note = await generateClaim(id);
              const gen = note ? isGenerated(note) : true;
              patchEncClaim(id, { generated: gen, claimStage: gen ? "Ready to submit" : "Needs review" });
              return { ok: true };
            } catch (e) { const d = e?.response?.data?.detail; throw new Error(typeof d === "string" ? d : "The claim could not be generated."); }
          };
        } catch (e) {}
        winRef.current = win; dRef.current = D;
        bindEncClaims(win, D, token, uuid);
        rebindRef.current = () => bindEncClaims(win, D, sessionStorage.getItem("revq.token") || token, uuid);
        bindDocs(win, D, token, uuid);
        await bindIndicators(win, D, token);
        setState("ready");
        refreshStickyCount();
      } catch (e) {
        setErrMsg((e && e.message) || "The chart could not be loaded.");
        setState("error");
      }
    };
    tick();
  };

  return (
    <div
      className={embedded ? undefined : "rvq-fade-in"}
      style={embedded
        ? { position: "absolute", inset: 0, display: "flex", background: "#f5f5f7" }
        : { position: "fixed", inset: 0, display: "flex", background: "#f5f5f7" }}
      data-testid="chart-v2-page"
    >
      {!embedded && <Sidebar activeChart={patientName || undefined} />}
      <div style={{ position: "relative", flex: 1, minWidth: 0, background: "#f5f5f7", paddingTop: !embedded && returnTo ? 44 : 0, paddingRight: teleErrand ? teleW : 0 }}>
        {!embedded && returnTo ? (
          <div
            data-testid="chart-v2-backbar"
            style={{ position: "absolute", top: 0, left: 0, right: 0, height: 44, zIndex: 20, display: "flex", alignItems: "center", padding: "0 16px", background: "#fff", borderBottom: "1px solid #E9E9EC", fontFamily: "Inter, system-ui, sans-serif" }}
          >
            <button
              type="button"
              data-testid="chart-v2-back"
              onClick={goBack}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#141412", background: "transparent", border: "none", cursor: "pointer", padding: "6px 8px", borderRadius: 8 }}
            >
              <span style={{ fontSize: 17, lineHeight: 1, marginTop: -2 }}>‹</span> Back to claim
            </button>
          </div>
        ) : null}
        {state === "error" ? (
          <div
            style={{ position: "absolute", inset: 0, zIndex: 12, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, background: "#f5f5f7", color: "#141412", fontFamily: "Inter, system-ui, sans-serif" }}
            data-testid="chart-v2-error"
          >
            <div style={{ fontSize: 15, fontWeight: 700 }}>Couldn’t open this chart</div>
            <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 420, textAlign: "center", lineHeight: 1.5 }}>{errMsg}</div>
            <button
              onClick={() => { setState("loading"); setErrMsg(""); drive(); }}
              data-testid="chart-v2-retry"
              style={{ marginTop: 6, padding: "8px 16px", borderRadius: 999, border: "1px solid #d8d7d3", background: "#141412", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Try again
            </button>
          </div>
        ) : (
          // Loading cover — a chart-shaped skeleton mirroring the real layout
          // (302px patient rail + main column of cards). Hides the file's built-in
          // replay chart until ehiBind runs, then unmounts.
          state === "loading" && (
            <div
              data-testid="chart-v2-loading"
              aria-label="Loading patient chart"
              className="cv2-sk"
            >
              {/* patient rail (302px) — avatar/name, allergy chips, stacked mini-sections */}
              <aside className="cv2-sk-rail">
                <div className="cv2-sk-rhd">
                  <span className="cv2-shim cv2-sk-av" />
                  <div className="cv2-sk-rlines">
                    <span className="cv2-shim cv2-sk-name" />
                    <span className="cv2-shim cv2-sk-sub" />
                  </div>
                </div>
                <div className="cv2-sk-pills">
                  {[52, 68, 44].map((w, i) => <span key={i} className="cv2-shim cv2-sk-chip" style={{ width: w }} />)}
                </div>
                {[4, 3, 2, 3, 4, 2, 3].map((n, s) => (
                  <div key={s} className="cv2-sk-rsec">
                    <span className="cv2-shim cv2-sk-rlbl" />
                    <div className="cv2-sk-rgrid">
                      {Array.from({ length: n }).map((_, i) => <span key={i} className="cv2-shim cv2-sk-rrow" />)}
                    </div>
                  </div>
                ))}
              </aside>

              {/* main column — tab strip + stacked cards */}
              <main className="cv2-sk-centre">
                <div className="cv2-sk-tabs">
                  {[58, 62, 84, 60, 62].map((w, i) => <span key={i} className="cv2-shim cv2-sk-tab" style={{ width: w }} />)}
                </div>
                <div className="cv2-sk-pane">
                  {[4, 3, 3, 4, 3, 3].map((rows, c) => (
                    <div key={c} className="cv2-sk-card">
                      <div className="cv2-sk-shead">
                        <span className="cv2-shim cv2-sk-ct" />
                        <span className="cv2-shim cv2-sk-cmenu" />
                      </div>
                      <div className="cv2-sk-sbody">
                        {Array.from({ length: rows }).map((_, i) => (
                          <span key={i} className="cv2-shim cv2-sk-row" style={{ width: `${100 - i * 9}%` }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </main>
            </div>
          )
        )}
        <iframe
          ref={iframeRef}
          src="/onechart_v2.html"
          title="Patient chart"
          onLoad={drive}
          allow="microphone; camera"
          data-testid="chart-v2-frame"
          style={{ width: "100%", height: "100%", border: 0, display: "block" }}
        />
        {teleErrand ? (
          <THDock onWidth={setTeleW} onDrag={teleDrag}>
            <VisitScreen key={teleErrand.n} appt={teleErrand.appt} start={teleErrand.start} active={active} inChart tabKey={uuid} onBack={() => onTeleBack && onTeleBack()} onNoteReady={() => onTeleNoteReady && onTeleNoteReady()} />
          </THDock>
        ) : null}
        {encGate ? (
          <EncounterGate
            providers={encGate.providers}
            defaultNpi={encGate.defaultNpi}
            defaultDos={encGate.defaultDos}
            onStart={(meta) => {
              // surface the chosen provider + date on the chart's "running in revQ" strip
              try {
                const w = iframeRef.current && iframeRef.current.contentWindow;
                if (w) { w.AP_VISIT_META = meta; if (typeof w.render === "function") w.render(); }
              } catch (e) {}
              encGate.resolve(meta); setEncGate(null);
            }}
            onCancel={() => {
              try { const w = iframeRef.current && iframeRef.current.contentWindow; if (w) { w.AP_VISIT_META = null; } } catch (e) {}
              encGate.reject(new Error("cancelled")); setEncGate(null);
            }}
          />
        ) : null}
        {sticky ? (
          <StickyNotesPanel clientId={sticky.clientId} chartId={sticky.chartId} chartUuid={sticky.chartUuid} patientName={patientName} onClose={() => setSticky(null)} onActiveCount={pushStickyCount} />
        ) : null}
        {tele ? (
          <TelehealthVisits clientId={tele.clientId} ctx={tele.ctx} snapshot={tele.snapshot} onClose={() => setTele(null)} />
        ) : null}
        {docView ? (
          <div className="cv2-docmodal" data-testid="chart-doc-viewer" onClick={() => setDocView(null)}>
            <div className="cv2-docpanel" onClick={(e) => e.stopPropagation()}>
              <div className="cv2-dochead">
                <span className="cv2-docttl" title={docView.filename}>{docView.filename}</span>
                <button className="cv2-docclose" data-testid="chart-doc-viewer-close" aria-label="Close" onClick={() => setDocView(null)}>✕</button>
              </div>
              <div className="cv2-docbody">
                {docView.loading ? (
                  <div className="cv2-docmsg">Loading document…</div>
                ) : docView.error ? (
                  <div className="cv2-docmsg">{docView.error}</div>
                ) : (
                  <div className="cv2-docscroll pd-scope">
                    {docView.analysis ? <LensCard analysis={docView.analysis} /> : null}
                    <div className="cv2-docorig">
                      <div className="cv2-docorig-hd">Original · {docView.filename}</div>
                      {!docView.dataUrl ? (
                        <div className="cv2-docmsg" style={{ padding: 20 }}>Original not available.</div>
                      ) : (docView.content_type || "").indexOf("pdf") >= 0 ? (
                        <iframe title="document" src={docView.dataUrl} className="cv2-docframe" />
                      ) : (
                        <img alt={docView.filename} src={docView.dataUrl} className="cv2-docimg" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
        {insight ? (
          <div className="cv2-insmodal" data-testid="chart-insights-panel" onClick={() => setInsight(null)}>
            <aside className={`cv2-inspanel ${insight.tab === "ai" ? "wide" : ""}`} onClick={(e) => e.stopPropagation()}>
              <div className="cv2-inshead">
                {insight.tab === "ai" ? (
                  <div className="cv2-instabs"><span className={`cv2-instab on cv2-aittl-${insight.ai && insight.ai.panel}`} data-testid="chart-ai-title">{(AI_PANELS.find((x) => x.key === (insight.ai && insight.ai.panel)) || {}).label}</span></div>
                ) : (
                  <div className="cv2-instabs">
                    <button type="button" className={`cv2-instab ${insight.tab === "timeline" ? "on" : ""}`} data-testid="chart-insights-tab-timeline" onClick={() => setInsight((p) => ({ ...p, tab: "timeline" }))}>Timeline{insight.timeline.length ? ` · ${insight.timeline.length}` : ""}</button>
                    <button type="button" className={`cv2-instab ${insight.tab === "changes" ? "on" : ""}`} data-testid="chart-insights-tab-changes" onClick={() => setInsight((p) => ({ ...p, tab: "changes" }))}>Change history{insight.changes.length ? ` · ${insight.changes.length}` : ""}</button>
                  </div>
                )}
                <button className="cv2-insclose" data-testid="chart-insights-close" aria-label="Close" onClick={() => setInsight(null)}>✕</button>
              </div>
              <div className="cv2-insbody">
                {insight.loading ? (
                  <div className="cv2-insmsg">Loading…</div>
                ) : insight.tab === "ai" ? (
                  insight.ai.state === "idle" ? (
                    <div className="cv2-aipick" data-testid="chart-ai-idle">
                      <div className="cv2-aipicksub">{(AI_PANELS.find((x) => x.key === insight.ai.panel) || {}).desc}. Reads every source on this chart — live XML, ingested documents and visits. Uses Lumina credits.</div>
                      <button type="button" className={`cv2-airun cv2-aittl-${insight.ai.panel}`} data-testid="chart-ai-run" onClick={() => streamAi(insight.ai.panel)}>Run {(AI_PANELS.find((x) => x.key === insight.ai.panel) || {}).label}</button>
                    </div>
                  ) : (
                    <div data-testid="chart-ai-result">
                      {insight.ai.state === "nocredit" ? (
                        <div className="cv2-insmsg" data-testid="chart-summary-nocredit">Update Lumina credits in your account or contact support.</div>
                      ) : insight.ai.state === "error" ? (
                        <div className="cv2-insmsg">Couldn’t generate this right now.<div className="cv2-inshint"><button type="button" className="cv2-insretry" onClick={() => streamAi(insight.ai.panel)}>Try again</button></div></div>
                      ) : (
                        <div className="cv2-summary ai-plain" data-testid="chart-summary-body">
                          {insight.ai.panel === "lumina" ? <AiSteps steps={insight.ai.steps} /> : null}
                          {insight.ai.panel === "echo"
                            ? (insight.ai.text && <EchoMessages text={insight.ai.text} language={insight.ai.lang} />) || <AiProgress panel="echo" terms={chartReadTerms(dRef.current)} />
                            : insight.ai.text ? <AiMarkdown text={insight.ai.text} /> : <AiProgress panel={insight.ai.panel} terms={chartReadTerms(dRef.current)} />}
                          {insight.ai.state === "streaming" && insight.ai.text && insight.ai.panel !== "echo" ? <span className="cv2-caret" /> : null}
                        </div>
                      )}
                    </div>
                  )
                ) : insight.tab === "timeline" ? (
                  insight.timeline.length === 0 ? <div className="cv2-insmsg">No timeline events yet.</div> : (
                    <ol className="cv2-tl">
                      {insight.timeline.map((it, i) => (
                        <li className="cv2-tlrow" key={it.key || `${it.type}-${i}`} data-testid={`chart-tl-row-${i}`}>
                          <span className={`cv2-tldot cv2-tl-${it.type || "other"}`} />
                          <div className="cv2-tlmain">
                            <div className="cv2-tltop"><span className="cv2-tlttl">{it.title || it.kind || "Event"}</span><span className="cv2-tldate">{it.date || it.at || ""}</span></div>
                            {it.subtitle ? <div className="cv2-tlsub">{it.subtitle}</div> : null}
                            <div className="cv2-tltags">
                              <span className={`cv2-tltag cv2-tag-${it.type}`}>{it.type === "encounter" ? "Encounter" : it.type === "claim" ? "Claim" : "Message"}</span>
                              {it.ntc_source ? <span className="cv2-tltag">{it.ntc_source}</span> : null}
                              {it.status ? <span className="cv2-tltag">{it.status}</span> : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )
                ) : (
                  insight.changes.length === 0 ? <div className="cv2-insmsg">No recorded changes yet.<div className="cv2-inshint">Live-XML refreshes and document ingests will appear here.</div></div> : (
                    <ol className="cv2-tl">
                      {insight.changes.map((c, i) => (
                        <li className="cv2-tlrow" key={i} data-testid={`chart-change-row-${i}`}>
                          <span className="cv2-tldot cv2-tl-change" />
                          <div className="cv2-tlmain">
                            <div className="cv2-tltop"><span className="cv2-tlttl">{c.label || c.action || "Change"}</span><span className="cv2-tldate">{(c.time || "").slice(0, 10)}</span></div>
                            <div className="cv2-tlsub">{c.user_name || c.user_email || "System"}{c.ip ? ` · ${c.ip}` : ""}</div>
                            {(c.lines_added || c.lines_removed) ? <div className="cv2-tltags">{c.lines_added ? <span className="cv2-tltag cv2-tag-add">+{c.lines_added}</span> : null}{c.lines_removed ? <span className="cv2-tltag cv2-tag-del">−{c.lines_removed}</span> : null}</div> : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )
                )}
              </div>
            </aside>
          </div>
        ) : null}
      </div>
      {!embedded ? <div className="rvq-leave-veil" data-on={leaving} aria-hidden="true" /> : null}
      <style>{`
        ${AI_PANEL_CSS}
        ${AI_PROGRESS_CSS}
        .cv2-airun{margin-top:14px;appearance:none;border:1px solid transparent;border-radius:999px;padding:9px 18px;font:700 12.5px Inter,system-ui,sans-serif;cursor:pointer;transition:filter .14s,transform .14s}
        .cv2-airun:hover{filter:brightness(.96);transform:translateY(-1px)}
        .cv2-aittl-summary{color:#1F5FA8!important}.cv2-airun.cv2-aittl-summary{background:#E8F1FC;border-color:#CFE0F6}
        .cv2-aittl-lumina{color:#5B3FB8!important}.cv2-airun.cv2-aittl-lumina{background:#F0ECFC;border-color:#DDD3F7}
        .cv2-aittl-echo{color:#0F7A6E!important}.cv2-airun.cv2-aittl-echo{background:#E5F5F2;border-color:#C8EAE3}
        .cv2-insbtn{position:absolute;right:16px;bottom:18px;z-index:3500;display:inline-flex;align-items:center;gap:7px;height:38px;padding:0 15px;border-radius:999px;border:1px solid #E0DFDB;background:#141412;color:#fff;font:600 12.5px Inter,system-ui,sans-serif;cursor:pointer;box-shadow:0 10px 26px -10px rgba(20,20,18,.45);transition:transform .14s,box-shadow .14s}
        .cv2-insbtn:hover{transform:translateY(-1px);box-shadow:0 14px 30px -10px rgba(20,20,18,.55)}
        .cv2-insmodal{position:fixed;inset:0;z-index:4200;background:rgba(20,20,18,.42);display:flex;justify-content:flex-end;animation:cv2df .18s ease}
        .cv2-inspanel{width:min(460px,96vw);height:100%;background:#fff;box-shadow:-18px 0 60px rgba(20,20,18,.3);display:flex;flex-direction:column;overflow:hidden;animation:cv2slide .22s cubic-bezier(.4,0,.2,1)}
        @keyframes cv2slide{from{transform:translateX(40px);opacity:.4}to{transform:none;opacity:1}}
        .cv2-inshead{flex:none;display:flex;align-items:center;gap:10px;padding:10px 12px 0 16px;border-bottom:1px solid #E9E9EC}
        .cv2-instabs{display:flex;gap:18px;flex:1}
        .cv2-instab{appearance:none;border:0;background:none;padding:6px 0 12px;font:650 13px Inter,system-ui,sans-serif;color:#9B9B97;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px}
        .cv2-instab.on{color:#141412;border-bottom-color:#141412}
        .cv2-insclose{flex:none;width:30px;height:30px;border-radius:8px;border:1px solid #E9E9EC;background:#fff;color:#6C6C69;cursor:pointer;margin-bottom:8px}
        .cv2-insclose:hover{border-color:#141412;color:#141412}
        .cv2-insbody{flex:1;min-height:0;overflow:auto;background:#F7F7F9;padding:16px}
        .cv2-insmsg{font:600 13px Inter,system-ui,sans-serif;color:#6C6C69;padding:48px 20px;text-align:center}
        .cv2-inshint{font-weight:500;font-size:11.5px;color:#9B9B97;margin-top:6px}
        .cv2-tl{list-style:none;margin:0;padding:0;position:relative}
        .cv2-tl:before{content:"";position:absolute;left:5px;top:4px;bottom:4px;width:2px;background:#E6E6E4}
        .cv2-tlrow{position:relative;display:flex;gap:14px;padding:0 0 16px 0}
        .cv2-tldot{position:relative;z-index:1;flex:none;width:12px;height:12px;border-radius:50%;margin-top:3px;background:#9B9B97;box-shadow:0 0 0 3px #F7F7F9}
        .cv2-tl-encounter{background:#5856D6}.cv2-tl-claim{background:#0E9F6E}.cv2-tl-message{background:#FF9500}.cv2-tl-change{background:#6C6C69}
        .cv2-tlmain{flex:1;min-width:0;background:#fff;border:1px solid #E9E9EC;border-radius:11px;padding:11px 13px}
        .cv2-tltop{display:flex;align-items:baseline;gap:10px}
        .cv2-tlttl{flex:1;min-width:0;font:700 13px Inter,system-ui,sans-serif;color:#141412}
        .cv2-tldate{flex:none;font:600 11px Inter,system-ui,sans-serif;color:#9B9B97;font-variant-numeric:tabular-nums}
        .cv2-tlsub{font:500 12px Inter,system-ui,sans-serif;color:#6C6C69;margin-top:3px;line-height:1.4}
        .cv2-tltags{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
        .cv2-tltag{font:700 9.5px Inter,system-ui,sans-serif;letter-spacing:.03em;text-transform:uppercase;color:#6C6C69;background:#F0EFEC;border-radius:999px;padding:3px 8px}
        .cv2-tag-encounter{color:#4a4ad6;background:#ecebfb}.cv2-tag-claim{color:#0b7a55;background:#e5f6ef}.cv2-tag-message{color:#9a5a00;background:#fef7ec}
        .cv2-tag-add{color:#0b7a55;background:#e5f6ef}.cv2-tag-del{color:#b42318;background:#fde7e4}
        .cv2-summary{background:#fff;border:1px solid #E9E9EC;border-radius:12px;padding:16px 18px;font:400 13px/1.6 Inter,system-ui,sans-serif;color:#2b2b28}
        .cv2-summary h3{font-size:14px;font-weight:800;margin:14px 0 6px;color:#141412}
        .cv2-summary h4{font-size:12.5px;font-weight:700;margin:12px 0 5px;color:#3a3a37}
        .cv2-summary li{margin:3px 0 3px 2px;list-style:none;padding-left:14px;position:relative}
        .cv2-summary li:before{content:"•";position:absolute;left:0;color:#5856D6}
        .cv2-summary strong{color:#141412}
        .cv2-caret{display:inline-block;width:7px;height:14px;background:#5856D6;margin-left:2px;vertical-align:-2px;animation:cv2blink 1s steps(2) infinite}
        @keyframes cv2blink{0%,50%{opacity:1}51%,100%{opacity:0}}
        .cv2-insretry{appearance:none;border:1px solid #E0DFDB;background:#141412;color:#fff;border-radius:999px;padding:6px 14px;font:600 12px Inter,system-ui,sans-serif;cursor:pointer;margin-top:4px}
        .cv2-aipick{display:flex;flex-direction:column;gap:10px}
        .cv2-aipicktitle{font:800 14px Inter,system-ui,sans-serif;color:#141412;margin-top:2px}
        .cv2-aipicksub{font:500 12px/1.5 Inter,system-ui,sans-serif;color:#8a8a86;margin:-4px 0 6px}
        .cv2-aiopt{display:flex;flex-direction:column;gap:3px;text-align:left;background:#fff;border:1px solid #E9E9EC;border-radius:12px;padding:13px 15px;cursor:pointer;transition:border-color .14s,transform .14s,box-shadow .14s}
        .cv2-aiopt:hover{border-color:#141412;transform:translateY(-1px);box-shadow:0 10px 24px -14px rgba(20,20,18,.4)}
        .cv2-aioptlbl{font:700 13.5px Inter,system-ui,sans-serif;color:#141412}
        .cv2-aioptdesc{font:500 12px Inter,system-ui,sans-serif;color:#8a8a86}
        .cv2-aiback{appearance:none;border:0;background:none;color:#5856D6;font:600 12px Inter,system-ui,sans-serif;cursor:pointer;padding:0 0 10px}
        .cv2-airesttl{font:800 14px Inter,system-ui,sans-serif;color:#141412;margin:0 0 10px}
        .cv2-docmodal{position:fixed;inset:0;z-index:4000;background:rgba(20,20,18,.5);display:flex;align-items:center;justify-content:center;padding:28px;animation:cv2df .18s ease}
        @keyframes cv2df{from{opacity:0}to{opacity:1}}
        .cv2-docpanel{width:min(900px,96vw);height:min(92vh,960px);background:#fff;border-radius:14px;box-shadow:0 24px 70px rgba(20,20,18,.4);display:flex;flex-direction:column;overflow:hidden}
        .cv2-dochead{flex:none;display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid #E9E9EC}
        .cv2-docttl{font:700 13px Inter,system-ui,sans-serif;color:#141412;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .cv2-docclose{margin-left:auto;flex:none;width:30px;height:30px;border-radius:8px;border:1px solid #E9E9EC;background:#fff;color:#6C6C69;cursor:pointer;font-size:13px}
        .cv2-docclose:hover{border-color:#141412;color:#141412}
        .cv2-docbody{flex:1;min-height:0;background:#F5F5F7;display:flex;flex-direction:column;overflow:auto}
        .cv2-docscroll{padding:16px;display:flex;flex-direction:column;gap:14px}
        .cv2-docorig{background:#fff;border:1px solid #E9E9EC;border-radius:12px;overflow:hidden}
        .cv2-docorig-hd{font:700 11px Inter,system-ui,sans-serif;letter-spacing:.04em;text-transform:uppercase;color:#9B9B97;padding:10px 14px;border-bottom:1px solid #F0F0F2}
        .cv2-docframe{width:100%;height:70vh;border:0;background:#fff}
        .cv2-docimg{display:block;max-width:100%;margin:0 auto}
        .cv2-docmsg{font:600 13px Inter,system-ui,sans-serif;color:#6C6C69;padding:40px;text-align:center}
        .cv2-sk{position:absolute;inset:0;z-index:10;background:#f5f5f7;overflow:hidden;
          font-family:Inter,system-ui,sans-serif;padding:14px;display:grid;
          grid-template-columns:302px minmax(0,1fr);grid-template-rows:minmax(0,1fr);gap:14px}
        .cv2-shim{display:block;flex:none;background:#DFDFE4;
          background-image:linear-gradient(90deg,#E2E2E7 0%,#CFCFD6 50%,#E2E2E7 100%);
          background-size:200% 100%;animation:cv2sk 1.2s linear infinite;border-radius:6px}
        @keyframes cv2sk{0%{background-position:200% 0}100%{background-position:-200% 0}}
        /* patient rail */
        .cv2-sk-rail{background:#fff;border:1px solid #E9E9EC;border-radius:16px;
          box-shadow:0 1px 2px rgba(20,20,18,.04);display:flex;flex-direction:column;overflow:hidden}
        .cv2-sk-rhd{display:flex;align-items:center;gap:12px;padding:16px 16px 14px;border-bottom:1px solid #F0F0F2}
        .cv2-sk-av{width:44px;height:44px;border-radius:50%}
        .cv2-sk-rlines{display:flex;flex-direction:column;gap:8px;min-width:0;flex:1}
        .cv2-sk-name{width:70%;height:14px}
        .cv2-sk-sub{width:90%;height:10px;border-radius:4px}
        .cv2-sk-pills{display:flex;gap:6px;padding:12px 16px;border-bottom:1px solid #F0F0F2}
        .cv2-sk-chip{height:18px;border-radius:999px}
        .cv2-sk-rsec{padding:12px 16px;border-bottom:1px solid #F0F0F2;display:flex;flex-direction:column;gap:9px}
        .cv2-sk-rsec:last-child{border-bottom:0}
        .cv2-sk-rlbl{width:40%;height:9px;border-radius:4px}
        .cv2-sk-rgrid{display:grid;grid-template-columns:1fr 1fr;gap:7px}
        .cv2-sk-rrow{height:10px;border-radius:4px}
        /* main column */
        .cv2-sk-centre{display:flex;flex-direction:column;gap:12px;min-width:0}
        .cv2-sk-tabs{display:flex;gap:16px;padding:4px 4px 12px;border-bottom:1px solid #E9E9EC}
        .cv2-sk-tab{height:12px;border-radius:4px}
        .cv2-sk-pane{display:flex;flex-direction:column;gap:12px}
        .cv2-sk-card{background:#fff;border:1px solid #E9E9EC;border-radius:14px;box-shadow:0 1px 2px rgba(20,20,18,.04);overflow:hidden}
        .cv2-sk-shead{display:flex;align-items:center;justify-content:space-between;padding:13px 16px;border-bottom:1px solid #F0F0F2}
        .cv2-sk-ct{width:34%;height:12px}
        .cv2-sk-cmenu{width:52px;height:12px;border-radius:4px}
        .cv2-sk-sbody{padding:14px 16px;display:flex;flex-direction:column;gap:9px}
        .cv2-sk-row{height:10px;border-radius:4px}
        @media (max-width:820px){.cv2-sk{grid-template-columns:1fr}.cv2-sk-rail{display:none}}
        @media (prefers-reduced-motion: reduce){.cv2-shim{animation:none}}
      `}</style>
    </div>
  );
}
